'use client';

import { useEffect, useState } from 'react';
import { useParams } from 'next/navigation';
import Link from 'next/link';
import { categoryQuizMapping } from '@/lib/category-quiz-bank';
import { ArrowLeft, Clock } from 'lucide-react';

interface Question {
  courseId: string;
  courseTitle: string;
  tier: 'memory' | 'understand' | 'apply';
  questionIndex: number;
  questionText: string;
  options: string[];
  correctIndex: number;
}

interface QuizData {
  category: string;
  categoryName: string;
  totalQuestions: number;
  questionsPerTier: number;
  questions: Question[];
}

export default function CategoryQuizPage() {
  const params = useParams();
  const categoryId = (params?.category as string) || 'performance';
  const [mounted, setMounted] = useState(false);
  const [quizData, setQuizData] = useState<QuizData | null>(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [submitted, setSubmitted] = useState(false);
  
  useEffect(() => {
    setMounted(true);
    
    // 获取题目
    fetch(`/api/quiz/category?category=${categoryId}`)
      .then(res => res.json())
      .then(data => {
        setQuizData(data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, [categoryId]);
  
  const handleAnswer = (questionIndex: number, optionIndex: number) => {
    if (submitted) return;
    setAnswers(prev => ({ ...prev, [questionIndex]: optionIndex }));
  };
  
  const submitQuiz = () => {
    if (!quizData || submitted) return;
    setSubmitted(true);
    
    // 计算分数
    let memory = 0, understand = 0, apply = 0;
    quizData.questions.forEach(q => {
      const answered = answers[q.questionIndex];
      if (answered !== undefined && answered === q.correctIndex) {
        if (q.tier === 'memory') memory++;
        else if (q.tier === 'understand') understand++;
        else apply++;
      }
    });
    
    // 跳转结果页
    const params = new URLSearchParams({
      category: categoryId,
      memory: memory.toString(),
      understand: understand.toString(),
      apply: apply.toString(),
      total: (memory + understand + apply).toString(),
      perTier: quizData.questionsPerTier.toString(),
    });
    window.location.href = `/quiz-category-result?${params}`;
  };
  
  if (!mounted || loading) {
    return <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center">
      <div className="text-gray-500">加载题目中...</div>
    </div>;
  }
  
  if (!quizData || quizData.questions.length === 0) {
    return <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center">
      <div className="text-center">
        <div className="text-gray-500 mb-4">该职能暂无题库</div>
        <Link href="/quiz" className="text-cyan-400">返回测评列表</Link>
      </div>
    </div>;
  }
  
  const tierLabels: Record<string, { label: string; icon: string }> = {
    memory: { label: '记忆层', icon: '📋' },
    understand: { label: '理解层', icon: '🧩' },
    apply: { label: '应用层', icon: '🛠️' },
  };
  
  const tierQuestions = {
    memory: quizData.questions.filter(q => q.tier === 'memory'),
    understand: quizData.questions.filter(q => q.tier === 'understand'),
    apply: quizData.questions.filter(q => q.tier === 'apply'),
  };
  
  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* 头部 */}
        <Link href="/quiz" className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-300 mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          返回
        </Link>
        
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-white">{quizData.categoryName}综合测评</h1>
          <p className="text-sm text-gray-500 mt-2">
            共{quizData.totalQuestions}题 · 每层{quizData.questionsPerTier}题
          </p>
        </div>
        
        {/* 进度条 */}
        <div className="mb-8">
          <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all"
              style={{ width: `${(Object.keys(answers).length / quizData.totalQuestions) * 100}%` }}
            />
          </div>
          <div className="text-xs text-gray-500 mt-2 text-center">
            已答 {Object.keys(answers).length} / {quizData.totalQuestions} 题
          </div>
        </div>
        
        {/* 题目 */}
        <div className="space-y-8">
          {(['memory', 'understand', 'apply'] as const).map(tier => (
            <div key={tier}>
              <div className="flex items-center gap-2 mb-4 text-sm">
                <span>{tierLabels[tier].icon}</span>
                <span className="text-gray-400">{tierLabels[tier].label}</span>
                <span className="text-gray-600">({tierQuestions[tier].length}题)</span>
              </div>
              
              {tierQuestions[tier].map((q, idx) => (
                <div key={q.questionIndex} className="mb-6 bg-[#12121a]/60 border border-white/5 rounded-xl p-5">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="text-amber-400 font-bold text-sm">{idx + 1}.</div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">来源：{q.courseTitle}</div>
                      <div className="text-white">{q.questionText}</div>
                    </div>
                  </div>
                  
                  <div className="space-y-2 pl-6">
                    {q.options.map((opt, optIdx) => {
                      const isSelected = answers[q.questionIndex] === optIdx;
                      const isCorrect = submitted && optIdx === q.correctIndex;
                      const isWrong = submitted && isSelected && optIdx !== q.correctIndex;
                      
                      return (
                        <button
                          key={optIdx}
                          onClick={() => handleAnswer(q.questionIndex, optIdx)}
                          disabled={submitted}
                          className={`w-full text-left px-4 py-3 rounded-lg border transition-all text-sm
                            ${isSelected ? 'border-amber-500/50 bg-amber-500/10 text-white' : 'border-white/5 bg-white/2 text-gray-300 hover:border-white/10'}
                            ${isCorrect ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' : ''}
                            ${isWrong ? 'border-rose-500 bg-rose-500/10 text-rose-400 line-through' : ''}
                          `}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
        
        {/* 提交按钮 */}
        <div className="mt-8 text-center">
          <button
            onClick={submitQuiz}
            disabled={Object.keys(answers).length < quizData.totalQuestions || submitted}
            className="px-8 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold rounded-xl disabled:opacity-30 disabled:cursor-not-allowed transition-all hover:shadow-lg hover:shadow-amber-500/20"
          >
            {submitted ? '已提交' : '提交测评'}
          </button>
          <div className="text-xs text-gray-500 mt-2">
            {Object.keys(answers).length < quizData.totalQuestions 
              ? `还有${quizData.totalQuestions - Object.keys(answers).length}题未完成`
              : '点击提交查看结果'}
          </div>
        </div>
      </div>
    </div>
  );
}
